import { cn } from '@latitude/token-utils';
import type { ElementType } from 'react';

import { Box, type BoxProps } from './Box';
import { GRID_COLS, type GridTrackCount } from './grid-classes';
import { COLUMN_GAP, GAP, ROW_GAP, type SpacingStep } from './spacing';

type GridAlign = 'start' | 'center' | 'end' | 'stretch';
type GridJustify = 'start' | 'center' | 'end' | 'stretch';

const ALIGN: Record<GridAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const JUSTIFY: Record<GridJustify, string> = {
  start: 'justify-items-start',
  center: 'justify-items-center',
  end: 'justify-items-end',
  stretch: 'justify-items-stretch',
};

type GridOwnProps = {
  columns: GridTrackCount;
  gap?: SpacingStep;
  columnGap?: SpacingStep;
  rowGap?: SpacingStep;
  align?: GridAlign;
  justify?: GridJustify;
};

// Public API (design.md scope: Stack/Inline/Grid/GridItem). Uniform N-column
// grid — `columns` renders `repeat(n, minmax(0, 1fr))` via the static
// `grid-cols-N` lookup (see grid.ts). Non-uniform tracks fall through to the
// `className` escape hatch (design.md Non-Goals).
export type GridProps<E extends ElementType = 'div'> = GridOwnProps &
  Omit<BoxProps<E>, keyof GridOwnProps>;

export const Grid = <E extends ElementType = 'div'>({
  columns,
  gap,
  columnGap,
  rowGap,
  align,
  justify,
  className,
  ...rest
}: GridProps<E>) => {
  return (
    // @ts-expect-error - see Center.tsx for why: TS can't reduce two
    // independently-computed Omit<ComponentPropsWithoutRef<E>, ...>
    // expressions to prove they're structurally identical when E is an
    // unresolved generic, even though GridProps<E> is defined in terms of
    // BoxProps<E>.
    <Box<E>
      className={cn(
        'grid',
        GRID_COLS[columns],
        align !== undefined && ALIGN[align],
        justify !== undefined && JUSTIFY[justify],
        gap !== undefined && GAP[gap],
        columnGap !== undefined && COLUMN_GAP[columnGap],
        rowGap !== undefined && ROW_GAP[rowGap],
        className,
      )}
      {...rest}
    />
  );
};

Grid.displayName = 'Grid';
